import {
  clearPortfolioDataFromLocalStorage,
  getPortfolioFromLocalStorage,
  getPortfolioUpdatedAtFromLocalStorage,
  getPortfolioUsedAtFromLocalStorage,
  setPortfolioIsWritableToLocalStorage,
  setPortfolioToLocalStorage,
  setPortfolioUpdatedAtToLocalStorage,
  setPortfolioUsedAtToLocalStorage,
} from '@/localStorage'
import type { Portfolio } from '@/types'
import { handleCorruptedPortfolioDatabase } from '@/utils/corruptedDatabase'
import {
  createReadFromDatabase,
  createWriteToDatabase,
  deleteDatabase,
  overwriteDatabase,
} from '@/utils/database'
import {
  CorruptedDatabaseFileException,
  portfolioSyncInterval,
} from '@pkg/basic'
import type { SetupPortfolioReturnTypes } from '.'
import { migration } from '../../logic/migration'
import { syncAfterSetup } from '../../logic/syncAfterSetup'
import { syncDbFileToRemote } from '../../logic/syncDbFileToRemote'
import { acquireWriteOperation } from '../../states/writeOperation'
import { getPortfolioDbFileStream } from './getPortfolioDbFileStream'
import { resetPortfolioLastUsedAt } from './resetPortfolioLastUsedAt'

export const changePortfolio = async (params: {
  getToken: () => Promise<string | null>
  portfolios: readonly Portfolio[]
  id: string
}): Promise<SetupPortfolioReturnTypes> => {
  const { getToken, portfolios, id } = params

  const currentLocalPortfolio = getPortfolioFromLocalStorage()
  const lease = acquireWriteOperation({
    portfolioId: currentLocalPortfolio?.uuid ?? id,
    type: 'portfolio-switch',
  })
  try {
    const currentLastUsedAt = getPortfolioUsedAtFromLocalStorage()
    const currentUpdatedAt = getPortfolioUpdatedAtFromLocalStorage()
    const currentPortfolio = portfolios.find(
      (p) => p.uuid === currentLocalPortfolio?.uuid,
    )

    const [newDbFileStream] = await Promise.all([
      // 変更後 portfolio db file を取得する
      getPortfolioDbFileStream({ getToken, id }),
      // 変更前 portfolio DB が未同期なら remote に upload
      currentPortfolio !== undefined &&
      currentUpdatedAt !== undefined &&
      currentUpdatedAt >= currentPortfolio.updatedAt
        ? syncDbFileToRemote({
            portfolio: currentPortfolio,
            getToken,
          })
        : Promise.resolve(),
      // 変更前 portfolio の lastUsedAt を最終使用者が自分なら破棄する
      currentPortfolio !== undefined &&
      currentLastUsedAt !== undefined &&
      currentLastUsedAt >= currentPortfolio.lastUsedAt
        ? resetPortfolioLastUsedAt({ getToken, id: currentPortfolio.uuid })
        : Promise.resolve(),
    ])

    // local storage をクリア
    clearPortfolioDataFromLocalStorage()

    // 既存 portfolio DB 削除
    if (currentLocalPortfolio !== undefined) {
      await deleteDatabase(currentLocalPortfolio.uuid)
    }

    // 新 portfolio DB 保存
    const portfolio = portfolios.find((p) => p.uuid === id)
    if (portfolio === undefined) {
      throw new Error(`Portfolio not found: ${id}`)
    }
    if (newDbFileStream === undefined) {
      throw new Error(`Failed to get portfolio DB file: ${id}`)
    }
    await overwriteDatabase(portfolio.uuid, newDbFileStream)

    // local storage 更新
    setPortfolioToLocalStorage(portfolio)
    const readonly =
      portfolio.plan !== 'free' &&
      portfolio.plan !== 'mini' &&
      (!portfolio.isWriter ||
        portfolio.lastUsedAt > Date.now() - portfolioSyncInterval * 2)
    setPortfolioIsWritableToLocalStorage(!readonly)
    setPortfolioUpdatedAtToLocalStorage(portfolio.updatedAt)
    if (!readonly) {
      setPortfolioUsedAtToLocalStorage(portfolio.lastUsedAt)
    }

    // return data 作成
    const readFromDatabase = createReadFromDatabase(portfolio.uuid)
    const writeToDatabase = readonly
      ? undefined
      : createWriteToDatabase(portfolio.uuid)
    const portfolioPageData = {
      ...portfolio,
      readonly,
      readFromDatabase,
      writeToDatabase,
    }

    // migration
    try {
      await migration({
        readFromDatabase,
        writeToDatabase: writeToDatabase ?? readFromDatabase,
      })
    } catch (error) {
      if (error instanceof CorruptedDatabaseFileException) {
        await handleCorruptedPortfolioDatabase({
          error,
          site: 'setup-portfolio-change',
          context: {
            portfolioId: portfolio.uuid,
            previousPortfolioId: currentLocalPortfolio?.uuid,
            readonly,
          },
        })
      } else {
        throw error
      }
    }

    // sync 処理（完了を待たない）
    syncAfterSetup({
      ...portfolioPageData,
      getToken,
    })

    return {
      portfolios,
      portfolio: portfolioPageData,
    }
  } finally {
    lease.release()
  }
}
